/**
 * MagicBI Design System - Theme Provider
 * 
 * React context provider for theme management with support for
 * light/dark mode switching, system preference detection, and
 * localStorage persistence.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from '../design-system/themes';
import { duration, easing } from '../design-system/tokens/transitions';
import {
  applyCSSCustomProperties,
  getSystemDarkMode,
  getStoredTheme,
  setStoredTheme,
  createSystemThemeListener,
  THEME_RESET_EVENT,
  createThemePerformanceMonitor,
  debugTheme,
} from '../design-system/utils/theme-utils';
import type { ThemeContextType, ThemeProviderProps, ThemeMode, ThemePreference } from '../design-system/types/theme';

/** How long the colour cross-fade style stays on the page (a little over `duration.normal`). */
const THEME_FADE_MS = 300;

// Create theme context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = 'light',
  storageKey = 'magicbi-theme-mode',
  enableSystem = true,
}) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(defaultMode);
  const [isSystemDarkMode, setIsSystemDarkMode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasAppliedOnce = useRef(false);

  // Performance monitoring
  const performanceMonitor = useMemo(() => createThemePerformanceMonitor(), []);

  // Initialize from storage (an explicit choice always wins), else system preference or the default
  useEffect(() => {
    const systemDarkMode = getSystemDarkMode();
    setIsSystemDarkMode(systemDarkMode);

    const stored = getStoredTheme(storageKey);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      setPreferenceState(stored as ThemePreference);
    } else {
      setPreferenceState(enableSystem ? 'system' : defaultMode);
    }

    setIsInitialized(true);
  }, [defaultMode, storageKey, enableSystem]);

  // Follow live system changes (used when the preference is `system`)
  useEffect(() => {
    return createSystemThemeListener((isDark) => setIsSystemDarkMode(isDark));
  }, []);

  // Logout drops back to light (resetThemeToLight already cleared storage)
  useEffect(() => {
    const onReset = () => setPreferenceState('light');
    window.addEventListener(THEME_RESET_EVENT, onReset);
    return () => window.removeEventListener(THEME_RESET_EVENT, onReset);
  }, []);

  const resolvedMode: ThemeMode =
    preference === 'system' ? (isSystemDarkMode ? 'dark' : 'light') : preference;
  const mode = resolvedMode;

  // Get the current theme object
  const theme = useMemo(() => {
    return resolvedMode === 'dark' ? darkTheme : lightTheme;
  }, [resolvedMode]);

  // Apply CSS custom properties when theme changes
  useEffect(() => {
    if (!isInitialized) return;

    performanceMonitor.start();

    // Cross-fade colours on a user-driven change (not the first paint); skipped for reduced motion
    const reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (hasAppliedOnce.current && !reducedMotion) {
      const fade = document.createElement('style');
      fade.setAttribute('data-theme-fade', '');
      fade.innerHTML = `
        *, *::before, *::after {
          transition: background-color ${duration.normal} ${easing.smooth}, color ${duration.normal} ${easing.smooth},
            border-color ${duration.normal} ${easing.smooth}, fill ${duration.normal} ${easing.smooth},
            stroke ${duration.normal} ${easing.smooth} !important;
        }
      `;
      document.head.appendChild(fade);
      window.setTimeout(() => fade.remove(), THEME_FADE_MS);
    }
    hasAppliedOnce.current = true;

    // Apply CSS custom properties
    applyCSSCustomProperties(theme);

    // Add data attribute to document for CSS targeting
    document.documentElement.setAttribute('data-theme', resolvedMode);
    document.documentElement.dataset.mbTheme = resolvedMode;

    // Performance monitoring
    performanceMonitor.end(resolvedMode);

    // Debug theme in development
    if (process.env.NODE_ENV === 'development') {
      debugTheme(theme);
    }
  }, [theme, resolvedMode, isInitialized, performanceMonitor]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    setStoredTheme(next, storageKey);
  }, [storageKey]);

  const setMode = useCallback((next: ThemeMode) => setPreference(next), [setPreference]);

  // Toggle between light and dark mode
  const toggleMode = useCallback(() => {
    setPreference(resolvedMode === 'light' ? 'dark' : 'light');
  }, [resolvedMode, setPreference]);

  // Context value
  const contextValue = useMemo((): ThemeContextType => ({
    theme,
    mode,
    toggleMode,
    setMode,
    preference,
    setPreference,
    isSystemDarkMode,
    resolvedMode,
  }), [theme, mode, toggleMode, setMode, preference, setPreference, isSystemDarkMode, resolvedMode]);

  // Don't render until initialized to prevent flash
  if (!isInitialized) {
    return null;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

/** The theme context, or `undefined` outside a ThemeProvider (for code that must not throw there). */
export const useOptionalTheme = (): ThemeContextType | undefined => useContext(ThemeContext);

// Hook to get current theme mode
export const useThemeMode = () => {
  const { mode, resolvedMode, toggleMode, setMode, preference, setPreference } = useTheme();
  
  return {
    mode,
    preference,
    setPreference,
    resolvedMode,
    toggleMode,
    setMode,
    isDark: resolvedMode === 'dark',
    isLight: resolvedMode === 'light',
  };
};

// Hook to get theme colors with proper typing
export const useThemeColors = () => {
  const { theme } = useTheme();
  return theme.colors;
};

// Hook to get theme spacing
export const useThemeSpacing = () => {
  const { theme } = useTheme();
  return theme.spacing;
};

// Hook to get theme typography
export const useThemeTypography = () => {
  const { theme } = useTheme();
  return theme.typography;
};

// Hook to get theme shadows
export const useThemeShadows = () => {
  const { theme } = useTheme();
  return theme.shadows;
};

// Hook to get theme borders
export const useThemeBorders = () => {
  const { theme } = useTheme();
  return theme.borders;
};

// Hook to get theme transitions
export const useThemeTransitions = () => {
  const { theme } = useTheme();
  return theme.transitions;
};

// Hook to get responsive design utilities
export const useThemeBreakpoints = () => {
  const { theme } = useTheme();
  return theme.breakpoints;
};

// Export default
export default ThemeProvider;
